import { ref } from "vue";
import { type ChatStateThread, useChatStore } from "@/store/chat.store";
import type { ThreadDto } from "@/api/chat/dto/thread.dto";
import { LocalStorageKeys } from "@/data/storageKeys";

const DB_NAME = "userCache";
const THREADS_STORE_NAME = "threads";
const DB_VERSION = 1;

let db: IDBDatabase | null = null;

async function openDB(): Promise<IDBDatabase> {
  if (db) {
    return db;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(THREADS_STORE_NAME)) {
        database.createObjectStore(THREADS_STORE_NAME, { keyPath: "id" });
      }
    };
  });
}

export function useCache() {
  const cachedThreads = ref<ChatStateThread[]>([]);

  const load = async () => {
    const database = await openDB();
    const tx = database.transaction(THREADS_STORE_NAME, "readonly");
    const store = tx.objectStore(THREADS_STORE_NAME);

    return new Promise<ChatStateThread[]>((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        cachedThreads.value = request.result as ChatStateThread[];
        resolve(cachedThreads.value);
      };
      request.onerror = () => reject(request.error);
    });
  };

  const setUseCache = (value: boolean) => {
    localStorage.setItem(LocalStorageKeys.useCache, JSON.stringify(value));
  };

  const getUseCache = () => {
    // return JSON.parse(localStorage.getItem(LocalStorageKeys.useCache) || "false"); // we use cache by default
    return true; // we use cache by default
  };

  const setThreadsTimeFrom = (date: string): void => {
    localStorage.setItem(LocalStorageKeys.threadsTimeFrom, JSON.stringify(date));
  };

  const getThreadsTimeFrom = (): string => {
    const unparsedDate = localStorage.getItem(LocalStorageKeys.threadsTimeFrom);

    if (unparsedDate) {
      return JSON.parse(unparsedDate);
    }

    return "";
  };

  const clearThreadsTimeFrom = (): void => {
    localStorage.removeItem(LocalStorageKeys.threadsTimeFrom);
  };

  const getCachedThreads = async (updateStore = true): Promise<ChatStateThread[]> => {
    const db = await openDB();
    const tx = db.transaction(THREADS_STORE_NAME, "readonly");
    const store = tx.objectStore(THREADS_STORE_NAME);
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const threads = request.result;

        if (updateStore) {
          const chatStore = useChatStore();

          chatStore.addThreads(threads);
          chatStore.sortThreads();
        }

        resolve(threads);
      };

      request.onerror = () => {
        console.error("Ошибка при чтении тредов из кеша:", request.error);
        reject(request.error);
      };
    });
  };

  const clearCachedThreads = async (storeName = THREADS_STORE_NAME): Promise<boolean> => {
    try {
      const db = await openDB();
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);
      store.clear();

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => {
          resolve();

          if (storeName === THREADS_STORE_NAME) {
            clearThreadsTimeFrom();
          }
        };
        tx.onerror = () => {
          reject(tx.error);

          return false;
        };
      });

      console.log("Кэш тредов успешно очищен");
      return true;
    } catch (error) {
      console.error("Ошибка при очистке базы IndexedDB:", error);
      return false;
    }
  };

  const upsertCachedThread = async (thread: ThreadDto, updateStore = true): Promise<ThreadDto | undefined> => {
    const database = await openDB();
    const tx = database.transaction(THREADS_STORE_NAME, "readwrite");
    const store = tx.objectStore(THREADS_STORE_NAME);

    store.put(JSON.parse(JSON.stringify(thread)));

    return new Promise<ThreadDto | undefined>((resolve, reject) => {
      tx.oncomplete = () => {
        if (updateStore) {
          const chatStore = useChatStore();
          chatStore.upsertThread(thread);
        }

        return resolve(thread);
      };
      tx.onerror = () => {
        console.error(tx.error);
        reject(tx.error);
        return undefined;
      };
    });
  };

  return {
    cachedThreads,
    load,
    getCachedThreads,
    upsertCachedThread,
    setThreadsTimeFrom,
    getThreadsTimeFrom,
    clearCachedThreads,
    setUseCache,
    getUseCache,
  };
}
